import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useFiles } from '@/entities/file';
import { useInboxStore } from '@/features/inbox';
import { MailboxInfo } from '@/features/inbox';
import { useNotices } from '@/features/notices/useNotices';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import { InboxDto } from '@/shared/lib/sj-orm/models/inbox.dto';
import { CollectionItemRef, CollectionName } from '@/shared/lib/sj-orm/types';
import { files as fileUtils } from '@/shared/utils/files';
import { useModalsStore } from '@/widgets/modals';
import { FilePreviewData } from '@/widgets/modals/containers/FilePreviewModal/FilePreview';

import { inboxAllowedImageExtensions } from '../helpers';

export const useInboxActions = () => {
  const { t } = useTranslation('entities/inbox');

  const sjdb = useSJDatabase();
  const notices = useNotices();
  const modals = useModalsStore();
  const inboxStore = useInboxStore();
  const { decryptDocumentFile } = useFiles();

  return useMemo(() => {
    //#region Common
    const _fetchFile = async (item: Pick<FileMetaInfoDto, 'name' | 'filePath'>) => {
      const file = await decryptDocumentFile(item.filePath, { name: item.name });
      return file;
    };

    const _downloadFile = async (item: { filePath: string; name: string }) => {
      const file = await decryptDocumentFile(item.filePath);

      if (file) {
        fileUtils.downloadFile(file, item.name);
      }
    };

    const _getPreviewProps = (file: File, extension: string) => {
      let previewProps: FilePreviewData = {
        mode: 'file',
        file,
      };

      if (inboxAllowedImageExtensions.some((ext) => extension.toLowerCase().includes(ext))) {
        previewProps = {
          mode: 'fileInfo',
          fileInfo: {
            url: fileUtils.blobToUrl(file),
            fileType: 'image',
            name: file.name,
          },
        };
      }

      return previewProps;
    };

    const _attachFiles = async (
      target: { colName: CollectionName; id: DtoId },
      files: FileMetaInfoDto[],
    ) => {
      const filePaths = files.map((item) => item.filePath);

      if (target.colName === CollectionName.PRIVATE_DOCUMENTS) {
        const pd = sjdb.collections.privateDocuments?.findById(target.id);

        if (pd) {
          sjdb.collections.privateDocuments?.update({
            ...pd,
            attachments: (pd.attachments ?? []).concat(filePaths),
          });
        }
      } else if (target.colName === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS) {
        const pid = sjdb.collections.personalIdentifiersDocuments?.findById(target.id);

        if (pid) {
          sjdb.collections.personalIdentifiersDocuments?.update({
            ...pid,
            attachments: (pid.attachments ?? []).concat(filePaths),
          });
        }
      }

      files.forEach((item) => {
        sjdb.collections.fileMetaInfos?.update({
          ...item,
          isFileAttached: true,
        });
      });

      notices.success('File(s) attached');
    };

    const _attachEmails = async (target: CollectionItemRef, emails: InboxDto[]) => {
      const emailsIds = emails.map((item) => item.id);
      let attached = false;

      if (target.colName === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS) {
        const item = sjdb.collections.personalIdentifiersDocuments?.findById(target.id);

        if (item) {
          sjdb.collections.personalIdentifiersDocuments?.update({
            ...item,
            emails: (item.emails ?? []).concat(emailsIds),
          });
          attached = true;
        }
      }
      if (target.colName === CollectionName.PRIVATE_DOCUMENTS) {
        const item = sjdb.collections.privateDocuments?.findById(target.id);

        if (item) {
          sjdb.collections.privateDocuments?.update({
            ...item,
            emails: (item.emails ?? []).concat(emailsIds),
          });
          attached = true;
        }
      }

      if (attached) {
        emails.forEach((item) => {
          sjdb.collections.inbox?.update({
            ...item,
            isEmailAttached: true,
          });
        });

        notices.success('File(s) attached');
      } else {
        notices.error('Attaching failed');
      }
    };

    const _attachEmailFiles = async (
      target: CollectionItemRef,
      emails: InboxDto,
      indexes?: number[],
    ) => {
      console.log(target, emails, indexes);
    };

    const _openEditComment = (
      file: Omit<FileMetaInfoDto, 'id'>,
      comment: string | undefined,
      onChange: (value: string) => void,
    ) => {
      modals.openModal({
        type: 'EditComment',
        // Should get type from somewhere
        file: { ...file, type: '' },
        comment: comment,
        onChange,
      });
    };

    //#region Files
    const attachFile = (item: FileMetaInfoDto) => {
      modals.openModal({
        type: 'SelectCollectionItem',
        onSelect: (data) => _attachFiles(data, [item]),
      });
    };

    const attachFiles = (items: FileMetaInfoDto[]) => {
      modals.openModal({
        type: 'SelectCollectionItem',
        onSelect: (data) => _attachFiles(data, items),
      });
    };

    const downloadFile = async (item: FileMetaInfoDto) => {
      await _downloadFile(item);
      notices.success('Downloading file');
    };

    const downloadFiles = (items: FileMetaInfoDto[]) => {
      items.forEach((item) => _downloadFile(item));
      notices.success('Downloading files');
    };
    const removeFile = (item: FileMetaInfoDto) => {
      modals.openModal({
        type: 'ConfirmModal',
        title: t('removeConfirm', { name: 'file' }),
        variant: 'delete',
        onConfirm: () => {
          sjdb.collections.fileMetaInfos?.removeById(item.id);
          notices.success(t('notices.fileDeleted'));
        },
      });
    };

    const removeFiles = (items: FileMetaInfoDto[]) => {
      modals.openModal({
        type: 'ConfirmModal',
        title: t('removeConfirm', { name: 'file' }),
        variant: 'delete',
        onConfirm: () => {
          const ids = items.map((item) => item.id);

          sjdb.collections.fileMetaInfos?.removeMany((item) => ids.includes(item.id));
          notices.success(t('notices.fileDeleted'));
        },
      });
    };

    const openEditFileComment = (item: FileMetaInfoDto) => {
      _openEditComment(item, item.comment, (comment) => {
        sjdb.collections.fileMetaInfos?.update({ ...item, comment });
      });
    };

    const openFilePreview = async (item: FileMetaInfoDto) => {
      const file = await _fetchFile(item);
      if (!file) {
        notices.error(t('errors.openPreview'));
        return;
      }
      let comment = item.comment;

      const previewProps = _getPreviewProps(file, item.extension);

      modals.openModal({
        type: 'FilePreviewModal',
        ...previewProps,
        hidePrint: true,
        onRemove: () => {
          sjdb.collections.fileMetaInfos?.removeById(item.id);
          notices.success(t('notices.fileDeleted'));
        },
        onEditComment: () => {
          _openEditComment(item, comment, (res) => {
            comment = res;
            sjdb.collections.fileMetaInfos?.update({ ...item, comment: res });
          });
        },
        onAttach: () => attachFile(item),
      });
    };

    const openUploadFiles = () => {
      modals.openModal({
        type: 'UploadFiles',
        onUploaded: () => {
          notices.success(t('notices.filesUploaded'));
        },
      });
    };

    //#region Emails
    const removeEmail = (item: InboxDto, onRemove?: () => void) => {
      modals.openModal({
        type: 'ConfirmModal',
        title: t('removeConfirm', { name: 'email' }),
        variant: 'delete',
        onConfirm: () => {
          sjdb.collections.inbox?.removeById(item.id);
          notices.success(t('notices.emailDeleted'));
          onRemove?.();
        },
      });
    };

    const removeEmails = (items: InboxDto[]) => {
      modals.openModal({
        type: 'ConfirmModal',
        title: t('removeConfirm', { name: 'email' }),
        variant: 'delete',
        onConfirm: () => {
          const ids = items.map((item) => item.id);

          sjdb.collections.inbox?.removeMany((item) => ids.includes(item.id));
          notices.success(t('notices.emailDeleted'));
        },
      });
    };

    const openInboxEmailConfig = (emailInfo?: MailboxInfo) => {
      modals.openModal({
        type: 'InboxEmailConfig',
        emailInfo,
        onSave: (info) => inboxStore.setMailbox(info),
      });
    };
    const openEmailInfo = (email: InboxDto, isAttaching: boolean = true, onRemove?: () => void) => {
      modals.openModal({
        type: 'InboxEmailInfo',
        emailId: email.id,
        onAttach: isAttaching ? () => attachEmails([email]) : undefined,
        onDownload: () => downloadEmailFiles(email),
        onRemove,
      });
    };

    //#region Emails file
    const attachEmails = (emails: InboxDto[]) => {
      modals.openModal({
        type: 'SelectCollectionItem',
        onSelect: (target) => _attachEmails(target, emails),
      });
    };

    const attachEmailFiles = (email: InboxDto, indexes?: number[]) => {
      modals.openModal({
        type: 'SelectCollectionItem',
        // TODO attach files
        onSelect: (target) => _attachEmailFiles(target, email, indexes),
      });
    };

    const downloadEmailFiles = (email: InboxDto, indexes?: number[]) => {
      const list = indexes
        ? email.attachments.filter((_, index) => indexes.includes(index))
        : email.attachments;

      list.forEach((item) => _downloadFile(item));
      notices.success('Downloading files');
    };

    const downloadEmailsFiles = (items: InboxDto[]) => {
      items.forEach((iInbox) => {
        iInbox.attachments.forEach((item) => _downloadFile(item));
      });
      notices.success('Downloading files');
    };

    const removeEmailFile = (email: InboxDto, index: number) => {
      modals.openModal({
        type: 'ConfirmModal',
        title: t('removeConfirm', { name: 'file' }),
        variant: 'delete',
        onConfirm: () => {
          const emailItem = sjdb.collections.inbox?.findById(email.id);
          if (!emailItem) return;
          // TODO move to entities actions
          const newItem = {
            ...emailItem,
            attachments: emailItem.attachments.filter((_, attachIndex) => attachIndex !== index),
          };

          sjdb.collections.inbox?.update(newItem);
          notices.success(t('notices.fileDeleted'));
        },
      });
    };

    const openEmailFilePreview = async (
      email: InboxDto,
      index: number,
      isAttaching: boolean = true,
    ) => {
      const fileInfo = email.attachments[index];
      const file = await _fetchFile(fileInfo);

      if (!file) {
        notices.error(t('errors.openPreview'));
        return;
      }

      const previewProps = _getPreviewProps(file, fileInfo.extension);

      modals.openModal({
        type: 'FilePreviewModal',
        ...previewProps,
        hidePrint: true,
        onRemove: () => {
          removeEmailFile(email, index);
          notices.success(t('notices.fileDeleted'));
        },
        onEditComment: () => openEditEmailFileComment(email, index),
        onAttach: isAttaching ? () => attachEmailFiles(email) : undefined,
      });
    };

    const openEditEmailFileComment = (email: InboxDto, index: number) => {
      const file = email.attachments[index];

      _openEditComment(file, file.comment, (comment) => {
        const emailItem = sjdb.collections.inbox?.findById(email.id);
        if (!emailItem) {
          return;
        }

        const newItem = {
          ...emailItem,
          attachments: emailItem.attachments.map((iAttach, attachIndex) => {
            return attachIndex === index
              ? {
                  ...iAttach,
                  comment,
                }
              : iAttach;
          }),
        };

        sjdb.collections.inbox?.update(newItem);
      });
    };

    //#region Interface
    return {
      // files
      attachFile,
      attachFiles,
      downloadFile,
      downloadFiles,
      removeFile,
      removeFiles,
      openEditFileComment,
      openFilePreview,
      openUploadFiles,
      // emails
      removeEmail,
      removeEmails,
      openInboxEmailConfig,
      openEmailInfo,
      // email files
      attachEmails,
      attachEmailFiles,
      downloadEmailFiles,
      downloadEmailsFiles,
      removeEmailFile,
      openEmailFilePreview,
      openEditEmailFileComment,
    };
  }, [sjdb, decryptDocumentFile]);
};
